import React, { useState } from "react";
import { toast } from "sonner";
import { CalendarDays, RefreshCw, Trash2, Plus, Activity, AlertTriangle, CheckCircle2 } from "lucide-react";
import { StaffShell } from "@/components/shells";
import { Panel, Spinner, Btn, Tag, Field, Input, EmptyState } from "@/components/kit";
import { useAsync } from "@/lib/hooks";
import { api } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/vda";

const WEEK = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export default function BusinessCalendars() {
  const { data, loading, reload } = useAsync(() => api.adminListCalendars(), []);
  const syncStatus = useAsync(() => api.adminSyncStatus(), []);
  const [busy, setBusy] = useState(false);

  const defaultCal = (data?.calendars || []).find((c) => c.is_default) || (data?.calendars || [])[0];

  const holState = useAsync(
    () => (defaultCal ? api.adminListHolidays(defaultCal.id) : Promise.resolve({ holidays: [] })),
    [defaultCal?.id]
  );

  const [form, setForm] = useState({ holiday_date: "", name: "" });
  const [backfill, setBackfill] = useState(null);

  const sync = async () => {
    setBusy(true);
    try {
      const r = await api.adminSyncGovuk();
      toast.success(`Synced ${r.upserted} GOV.UK holidays (${r.division}).`);
      reload();
      holState.reload();
      syncStatus.reload();
    } catch (e) { toast.error(e.message || "Sync failed"); }
    finally { setBusy(false); }
  };

  const addHoliday = async () => {
    if (!form.holiday_date) return toast.error("Pick a date");
    setBusy(true);
    try {
      await api.adminAddHoliday({ calendar_id: defaultCal.id, holiday_date: form.holiday_date, name: form.name });
      toast.success("Holiday added.");
      setForm({ holiday_date: "", name: "" });
      holState.reload();
      reload();
    } catch (e) { toast.error(e.message || "Could not add"); }
    finally { setBusy(false); }
  };

  const removeHoliday = async (id) => {
    setBusy(true);
    try {
      await api.adminDeleteHoliday(id);
      holState.reload();
      reload();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const previewBackfill = async () => {
    setBusy(true);
    try { setBackfill(await api.adminBackfillPreview()); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const applyBackfill = async () => {
    setBusy(true);
    try {
      const r = await api.adminBackfillApply();
      toast.success(`Applied working-day deadlines to ${r.applied} booking(s).`);
      setBackfill(null);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  if (loading) return <StaffShell><Spinner /></StaffShell>;

  return (
    <StaffShell>
      <p className="vda-tick text-[hsl(var(--vda-clay))]">Administration</p>
      <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-5">Business calendars</h1>

      {defaultCal && (
        <Panel className="p-5 mb-5" data-testid="default-calendar-panel">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 font-medium">
                <CalendarDays className="h-4 w-4 text-primary" /> {defaultCal.name}
                {defaultCal.is_default && <Tag tone="green">Default</Tag>}
              </div>
              <div className="mt-2 text-sm text-muted-foreground">
                {defaultCal.timezone} · {defaultCal.work_start?.slice(0, 5)}–{defaultCal.work_end?.slice(0, 5)} ·{" "}
                Division: {defaultCal.govuk_division}
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {WEEK.map((d) => (
                  <Tag key={d} tone={defaultCal[`${d}_is_working`] ? "green" : "muted"}>
                    {d.toUpperCase()}
                  </Tag>
                ))}
              </div>
              <div className="mt-2 text-sm">{defaultCal.holiday_count} holiday(s) loaded</div>
            </div>
            <Btn variant="clay" onClick={sync} loading={busy} data-testid="sync-govuk-btn">
              <RefreshCw className="h-4 w-4" /> Sync GOV.UK holidays
            </Btn>
          </div>
          {data?.sync_log?.[0] && (
            <div className="mt-3 text-xs text-muted-foreground" data-testid="last-sync-status">
              Last sync: {fmtDateTime(data.sync_log[0].created_at)} ·{" "}
              <span className={data.sync_log[0].status === "failed" ? "text-[hsl(var(--vda-red))]" : "text-primary"}>
                {data.sync_log[0].status}
              </span>{" "}· {data.sync_log[0].message}
            </div>
          )}
        </Panel>
      )}

      {(() => {
        const s = syncStatus.data || {};
        const failing = (s.failure_count || 0) >= 3;
        const ok = s.status === "success";
        const tone = failing ? "red" : ok ? "green" : "amber";
        const color = tone === "red" ? "hsl(var(--vda-red))" : tone === "green" ? "hsl(var(--vda-teal, var(--primary)))" : "hsl(var(--vda-amber))";
        const Icon = failing ? AlertTriangle : ok ? CheckCircle2 : Activity;
        return (
          <Panel className="p-5 mb-5" data-testid="calendar-health-strip">
            <div className="flex items-center gap-2 mb-3">
              <Activity className="h-4 w-4 text-[hsl(var(--vda-clay))]" />
              <h2 className="font-semibold">Calendar sync health</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="vda-tick text-muted-foreground">Status</div>
                <div className="mt-1 inline-flex items-center gap-1.5 font-medium" style={{ color }} data-testid="health-status">
                  <Icon className="h-4 w-4" /> {s.status ? s.status : "not run yet"}
                </div>
              </div>
              <div>
                <div className="vda-tick text-muted-foreground">Last success</div>
                <div className="mt-1 tabular-nums" data-testid="health-last-success">{s.last_success_at ? fmtDateTime(s.last_success_at) : "—"}</div>
              </div>
              <div>
                <div className="vda-tick text-muted-foreground">Consecutive failures</div>
                <div className="mt-1 tabular-nums" data-testid="health-failures">{s.failure_count || 0}</div>
              </div>
              <div>
                <div className="vda-tick text-muted-foreground">Next scheduled</div>
                <div className="mt-1 tabular-nums" data-testid="health-next">{s.next_scheduled_at ? fmtDateTime(s.next_scheduled_at) : "—"}</div>
              </div>
            </div>
            {failing && (
              <div className="mt-3 flex items-start gap-2 rounded-md border border-[hsl(var(--vda-red))]/40 bg-[hsl(var(--vda-red))]/5 p-3 text-sm text-[hsl(var(--vda-red))]" data-testid="health-alert">
                <AlertTriangle className="h-4 w-4 mt-0.5" />
                <span>GOV.UK sync has failed {s.failure_count} times in a row. Cached holidays are still in use — deadlines remain accurate. Administrators have been alerted.</span>
              </div>
            )}
          </Panel>
        );
      })()}

      <div className="grid lg:grid-cols-2 gap-5">
        <Panel className="p-5">
          <h2 className="font-semibold mb-3">Holidays</h2>
          <div className="flex items-end gap-2 mb-4">
            <div className="flex-1"><Field label="Date"><Input type="date" value={form.holiday_date} onChange={(e) => setForm({ ...form, holiday_date: e.target.value })} data-testid="holiday-date-input" /></Field></div>
            <div className="flex-1"><Field label="Name (optional)"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Company shutdown" data-testid="holiday-name-input" /></Field></div>
            <Btn onClick={addHoliday} loading={busy} data-testid="add-holiday-btn"><Plus className="h-4 w-4" /> Add</Btn>
          </div>
          {holState.loading ? <Spinner /> : (holState.data?.holidays || []).length === 0 ? (
            <EmptyState title="No holidays" hint="Sync GOV.UK or add one manually." />
          ) : (
            <div className="max-h-[420px] overflow-y-auto divide-y divide-border" data-testid="holiday-list">
              {holState.data.holidays.map((h) => (
                <div key={h.id} className="flex items-center justify-between py-2 text-sm" data-testid={`holiday-${h.holiday_date}`}>
                  <div>
                    <span className="font-medium tabular-nums">{fmtDate(h.holiday_date)}</span>{" "}
                    <span className="text-muted-foreground">{h.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Tag tone={h.source === "govuk" ? "muted" : "clay"}>{h.source}</Tag>
                    <button onClick={() => removeHoliday(h.id)} className="p-1 hover:text-[hsl(var(--vda-red))]" data-testid={`delete-holiday-${h.holiday_date}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel className="p-5">
          <h2 className="font-semibold mb-2">Migrate open bookings</h2>
          <p className="text-sm text-muted-foreground mb-3">Set working-day update deadlines on open bookings that have none. Preview first, then apply.</p>
          <div className="flex gap-2">
            <Btn variant="outline" onClick={previewBackfill} loading={busy} data-testid="backfill-preview-btn">Preview</Btn>
            {backfill && backfill.count > 0 && (
              <Btn variant="clay" onClick={applyBackfill} loading={busy} data-testid="backfill-apply-btn">Apply to {backfill.count}</Btn>
            )}
          </div>
          {backfill && (
            <div className="mt-4" data-testid="backfill-result">
              {backfill.count === 0 ? <p className="text-sm text-primary">All open bookings already have deadlines.</p> : (
                <div className="max-h-[360px] overflow-y-auto divide-y divide-border">
                  {backfill.rows.map((r) => (
                    <div key={r.id} className="py-2 text-sm">
                      <span className="vda-reg">{r.human_reference}</span> · {r.registration} · {r.customer}
                      <div className="text-xs text-muted-foreground">→ {fmtDateTime(r.proposed_due)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Panel>
      </div>
    </StaffShell>
  );
}
